import { isNotNullOrWhitespace } from "../../../framework/utils";
import { forEach, where, any } from "../../../framework/utils/collection";
import { IComponentSource, ComponentModel, compressComponentSource, MarkdownComponent } from "./core";
import { applyTextDecoration } from "./text-decoration";

export enum TableColumnAlignment {
  Default,
  Left,
  Center,
  Right,
}

export interface ITableCell {
  index: number;
  colspan: number;
  content: string;
}

export interface ITableRow {
  cells: ITableCell[];
}

export interface ITableComponentModel extends IComponentSource {
  columnAlignments: TableColumnAlignment[];
  header: ITableRow[];
  body: ITableRow[];
  footer: ITableRow[];
}

class TableComponentModel extends ComponentModel implements ITableComponentModel {
  public columnAlignments: TableColumnAlignment[] = [];
  public header: ITableRow[] = [];
  public body: ITableRow[] = [];
  public footer: ITableRow[] = [];
}

enum TableParseMode {
  Header,
  Body,
  Footer
}

const isHeaderRow = (line: string) => /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/.test(line);
const isFooterRow = (line: string) => /^\s*\|(?:\s*=+\s*\|)+\s*$/.test(line);

const initializeColumnAlignments = (table: ITableComponentModel) => {
  let colCount = 0;
  forEach(table.content, line => {
    const count = (line.content.match(/\|/g)?.length ?? 1) - 1;
    if(count > colCount) { colCount = count; }
  });

  table.columnAlignments[colCount - 1] = TableColumnAlignment.Default;
  table.columnAlignments.fill(TableColumnAlignment.Default);
}

const applyColumnAlignments = (headerRow: string, table: ITableComponentModel) => {
  const alignmentDefinitions = where(headerRow.split(/\s*\|\s*/g), d => isNotNullOrWhitespace(d));
  forEach(alignmentDefinitions, (def, index) => {
    const hasLeftIndicator = def[0] === ':';
    const hasRightIndicator = def[def.length - 1] === ':';

    let alignment = TableColumnAlignment.Default;
    if(hasLeftIndicator && !hasRightIndicator) { alignment = TableColumnAlignment.Left; }
    else if(!hasLeftIndicator && hasRightIndicator) { alignment = TableColumnAlignment.Right; }
    else if(hasLeftIndicator && hasRightIndicator) { alignment = TableColumnAlignment.Center; }

    table.columnAlignments[index!] = alignment;
  });
}

const parseTableRow = (line: string, rowLength: number) => {
  const rowSrc = line.replace(/^\s*\|/, '');
  const row: ITableRow = {
    cells: []
  };

  let columnsParsed = 0;
  const cellPattern = /[^|]*\|+/g;
  let result = cellPattern.exec(rowSrc);
  while(!!result) {
    const colspan = result[0].match(/\|/g)!.length;
    const index = columnsParsed;
    columnsParsed += colspan;

    let content = result[0].replace(/\s*\|+$/, '');
    content = applyTextDecoration(MarkdownComponent.Table, content).trim();

    row.cells.push({ index, colspan, content });

    result = cellPattern.exec(rowSrc);
  }

  const missingCells = rowLength - columnsParsed;
  for(let i = 0; i < missingCells; i++) {
    row.cells.push({
      index: columnsParsed + i,
      colspan: 1,
      content: ''
    });
  }

  return row;
}

export const buildTableComponentModel = (source: IComponentSource) => {
  compressComponentSource(source, false);
  const tableComponent = new TableComponentModel(source);
  initializeColumnAlignments(tableComponent);
  let parseMode = any(source.content, line => isHeaderRow(line.content)) ?
  TableParseMode.Header : TableParseMode.Body;
  
  const rowLength = tableComponent.columnAlignments.length;
  forEach(source.content, line => {
    if(parseMode === TableParseMode.Header) {
      if(isHeaderRow(line.content)) {
        applyColumnAlignments(line.content, tableComponent);
        parseMode = TableParseMode.Body;
      } else {
        tableComponent.header.push(parseTableRow(line.content, rowLength));
      }
    } else if(parseMode === TableParseMode.Body) {
      if(tableComponent.body.length > 0 && isFooterRow(line.content)) {
        parseMode = TableParseMode.Footer;
      } else {
        tableComponent.body.push(parseTableRow(line.content, rowLength));
      }
    } else { // footer content
      tableComponent.footer.push(parseTableRow(line.content, rowLength));
    }
  });

  return tableComponent;
}